import type { ChipColor } from '@/types/chip';
import type { AttendanceRecord } from '@/types/entities';
import { Box, Typography, Paper, Grid, Chip, IconButton, Divider, Link, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { VideoLibrary, CalendarMonth, Close, CheckCircle, Cancel, Event } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS: { [key: string]: ChipColor } = {
  Draft: 'default',
  Scheduled: 'info',
  Confirmed: 'success',
  Completed: 'secondary',
  Cancelled: 'error',
  Rescheduled: 'warning',
};

type SessionAttendance = AttendanceRecord;

interface StudentRecord {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  legalEntity?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
}

export const TutoringSessionDetail = () => {
  const navigate = useNavigate();

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/tutoring-sessions')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Tutoring Session"
            description="An individual tutoring session that occurred or is scheduled. Part of a schedule's session pattern."
            icon={<Event />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #e65100, #f57c00)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <VideoLibrary sx={{ fontSize: 48 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.sessionNumber || 'Session'}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {record.tutor?.id ? (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/tutors/${record.tutor.id}/show`)}
                      sx={{ color: 'white', textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer' }}
                    >
                      {record.tutorName || '—'}
                    </Link>
                  ) : (record.tutorName || '—')}
                  {' · '}{record.subject || '—'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <VideoLibrary color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Session Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Subject</Typography>
                    <Typography variant="body1" fontWeight={500}>{record.subject || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Tutor</Typography>
                    {record.tutor?.id ? (
                      <Typography variant="body1">
                        <Link
                          component="button"
                          variant="body1"
                          onClick={() => navigate(`/tutors/${record.tutor.id}/show`)}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                        >
                          {record.tutorName || '—'}
                        </Link>
                      </Typography>
                    ) : (
                      <Typography variant="body1">{record.tutorName || '—'}</Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Schedule</Typography>
                    <Typography variant="body1">
                      {record.schedule?.id ? (
                        <Link
                          component="button"
                          variant="body1"
                          onClick={() => navigate(`/tutoring-schedules/${record.schedule.id}/show`)}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                        >
                          {record.schedule.id.split(':').pop()?.substring(0, 12) || record.schedule.id}
                        </Link>
                      ) : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body1">
                          {record.startDateTime ? new Date(record.startDateTime).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Time</Typography>
                        <Typography variant="body1">
                          {record.startDateTime ? new Date(record.startDateTime).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <SessionAttendanceSection sessionId={record.id} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Session Notes</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Topics Covered</Typography>
                    <Typography variant="body1">{record.topicsCovered || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Tutor Notes</Typography>
                    <Typography variant="body1">{record.tutorNotes || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Homework Assigned</Typography>
                    <Typography variant="body1">{record.homeworkAssigned || '—'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      );
    }} />
  );
};

const SessionAttendanceSection = ({ sessionId }: { sessionId: string }) => {
  const navigate = useNavigate();
  const { data: attendanceData } = useGetList('attendance', { filter: { sessionId } });
  const { data: students } = useGetList('students', { pagination: { page: 1, perPage: 100 } });

  const records: SessionAttendance[] = attendanceData || [];

  const studentNameMap = new Map<string, string>();
  (students || []).forEach((s: StudentRecord) => {
    const first = s.firstName || s.legalEntity?.firstName || '';
    const last = s.lastName || s.legalEntity?.lastName || '';
    const name = `${first} ${last}`.trim() || s.id;
    studentNameMap.set(s.id, name);
  });

  const getStudentName = (studentId?: string) => {
    if (!studentId) return '—';
    return studentNameMap.get(studentId) || studentId;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Attendance</Typography>
        <Chip label={`${records.length} records`} size="small" sx={{ ml: 1 }} />
        <Button
          size="small"
          variant="outlined"
          sx={{ ml: 'auto', textTransform: 'none' }}
          onClick={() => navigate('/wizard/attendance')}
        >
          Capture Attendance
        </Button>
      </Box>
      {records.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((a: SessionAttendance) => {
              const StatusIcon = a.present ? CheckCircle : Cancel;
              return (
                <TableRow key={a.id}>
                  <TableCell>{getStudentName(a.student?.id)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={<StatusIcon />}
                      label={a.present ? 'Present' : 'Absent'}
                      size="small"
                      color={a.present ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>{a.durationMinutes ? `${a.durationMinutes} min` : '—'}</TableCell>
                  <TableCell>—</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No attendance records yet</Typography>
      )}
    </Paper>
  );
};
